import { GizmoData, SceneData, loadScene } from '../../SceneLoader';
import { MathUtils, Vector3 } from 'three';
import React, {
  Suspense,
  useCallback,
  useEffect,
  useReducer,
  useState,
} from 'react';

import CameraController from '../threejs/CameraController';
import Canvas3 from '../threejs/Canvas3';
import { Content } from '../../Content';
import ContentPopup from './content_popup/ContentPopup';
import Gizmo from '../threejs/Gizmo';
import PanoSphere from '../threejs/PanoSphere';

type State = {
  content: Content | null;
};

export type Action = {
  type: 'content-set';
  payload: { content: Content | null };
};

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'content-set':
      return { ...state, content: action.payload.content };
  }
};

export interface Props {
  sceneID: string;
}

export const SceneViewer: React.FC<Props> = (props) => {
  const [scene, setScene] = useState<SceneData | null>(null);

  const [isBGLoaded, setIsBGLoaded] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const [{ content: activeContent }, dispatch] = useReducer(reducer, {
    content: null,
  });

  const openGizmo = useCallback((gizmo: GizmoData) => {
    dispatch({
      type: 'content-set',
      payload: { content: gizmo.content },
    });
  }, []);

  useEffect(() => {
    console.log(props.sceneID);
    loadScene(`${props.sceneID}/scene.json`)
      .then((sceneData) => {
        setScene(sceneData);
      })
      .catch((reason) => {
        console.log(reason);
        setError('An issue occured while loading the scene.');
        setScene(null);
      });
  }, [props.sceneID]);

  const handleBGTextureLoaded = useCallback(() => {
    setIsBGLoaded(true);
  }, [setIsBGLoaded]);

  const handleBGTextureUnloaded = useCallback(() => {
    setIsBGLoaded(false);
  }, [setIsBGLoaded]);

  return (
    <div
      style={{
        width: '100vw',
        height: '100vh',
        position: 'absolute',
        top: '0',
      }}
    >
      {error !== null ? (
        <span style={{ color: 'white' }}>{error}</span>
      ) : (
        !isBGLoaded && (
          <div
            style={{
              position: 'absolute',
              width: '100%',
              height: '100%',
              display: 'flex',
            }}
          >
            <h1 style={{ color: 'white', margin: 'auto' }}>
              Loading, please wait...
            </h1>
          </div>
        )
      )}

      <Canvas3
        onCreated={({ camera }) => {
          if (scene?.initialRotation == null) return;
          const { 0: x, 1: y, 2: z } = scene.initialRotation;
          camera.rotation.x = MathUtils.degToRad(x);
          camera.rotation.y = MathUtils.degToRad(y);
          camera.rotation.z = MathUtils.degToRad(z);
          camera.updateProjectionMatrix();
        }}
      >
        <CameraController />
        {scene && (
          <Suspense fallback={<group />}>
            <PanoSphere
              position={[0, 0, 0]}
              texturePath={scene.bg}
              onTextureLoaded={handleBGTextureLoaded}
              onTextureUnloaded={handleBGTextureUnloaded}
            />
          </Suspense>
        )}
        {scene &&
          isBGLoaded &&
          scene.gizmos.map((value) => (
            <Gizmo
              key={value.id}
              gizmoData={value}
              onClick={() => {
                openGizmo(value);
              }}
              dispatch={dispatch}
              position={new Vector3(...value.direction).setLength(9)}
            />
          ))}
      </Canvas3>
      {isBGLoaded && (
        <div className="watermark-container">
          <span className="watermark-text">
            Powered by
            <span className="watermark-text-orange"> V2i Realtime</span>
          </span>
        </div>
      )}
      {activeContent && (
        <ContentPopup
          content={activeContent}
          onCloseRequested={() =>
            dispatch({ type: 'content-set', payload: { content: null } })
          }
        />
      )}
    </div>
  );
};
